/**
 * <nav-search> — the inline nav search bar (Figma nodes 1237:882 / 1237:894).
 * No <details>/<summary>: a plain button opens it, custom JS drives the
 * open/close state (adds/removes an `is-open` class on the host element),
 * reusing this theme's existing trapFocus/removeTrapFocus helpers
 * (assets/global.js) for the same focus behaviour details-modal.js gives
 * its other modals. Also owns:
 *   - showing/hiding the idle "Suggestions / Recent / Trending" panel based
 *     on whether the input has a value (predictive-search owns the
 *     has-query state once the user starts typing)
 *   - storing/rendering/removing "Recent Searches" in localStorage
 */
class NavSearch extends HTMLElement {
  static STORAGE_KEY = 'sammmm:nav-search:recent';
  static MAX_RECENT = 5;
  // sessionStorage bridge from a product tap (overlay here / SRP in
  // srp-infinite-scroll.js) to the view_item that fires on the PDP it opens.
  static VIEW_ITEM_KEY = 'sammmm:search:view_item';
  // sessionStorage bridge from a committed search (enter / recent / suggestion)
  // to the `search` event, which fires on the page the search LANDS on (SRP or
  // PDP) so it's present in that page's dataLayer, not the page being left.
  static SEARCH_KEY = 'sammmm:search:committed';

  constructor() {
    super();
    this.toggleButton = this.querySelector('[data-nav-search-toggle]');
    this.panel = this.querySelector('[data-nav-search-panel]');
    this.overlay = this.querySelector('[data-nav-search-overlay]');
    this.closeButton = this.querySelector('[data-nav-search-close]');
    this.input = this.querySelector('[data-nav-search-input]');
    this.form = this.querySelector('[data-nav-search-form]');
    this.idlePanel = this.querySelector('[data-nav-search-idle]');
    this.errorPanel = this.querySelector('[data-nav-search-error]');
    this.zeroPanel = this.querySelector('[data-nav-search-zero]');
    this.zeroTerm = this.querySelector('[data-nav-search-zero-term]');
    this.resultsSection = this.querySelector('[data-nav-search-results-section]');
    this.resultsGrid = this.querySelector('[data-nav-search-results-grid]');
    this.resultsSeeAll = this.querySelector('[data-nav-search-results-see-all]');
    this.recentSection = this.querySelector('[data-nav-search-recent-section]');
    this.recentList = this.querySelector('[data-nav-search-recent-list]');
    this.suggestionsDynamic = this.querySelector('[data-nav-search-suggestions-dynamic]');
    this.suggestionsList = this.querySelector('[data-nav-search-suggestions-list]');

    // GA4 (Search Bar PRD Epic 7): user_type rides on every payload; the
    // per-event timestamps debounce rapid open/close so counts don't inflate
    // (PRD 7.6); lastTypedValue detects the clear transition; the results
    // signature stops view_item_list refiring for an unchanged product set.
    this.userType = this.dataset.userType || 'logged_out';
    // Server-side recent searches (logged-in, cross-device): the theme calls
    // the backend DIRECTLY (no app proxy, no signature) and passes the Shopify
    // customer id (see recentCustomerId — native {{ customer.id }} or KwikPass).
    // `recentApiBase` is the ONE place the target backend is set (header setting
    // `recent_search_api_base`): prod = https://fam.sammmm.com, test = local.
    this.recentApiBase = (this.dataset.recentApi || 'https://fam.sammmm.com').replace(/\/$/, '');
    this.lastTypedValue = '';
    // The fullest term typed this session — grows while typing, never shrinks
    // on backspace — so search_cleared reports the whole keyword, not the last
    // character left before the field emptied.
    this.fullTypedValue = '';
    this.lastTrackedAt = {};
    this.lastResultsSignature = '';
  }

  connectedCallback() {
    this.onBodyClick = this.onBodyClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.equalizeProductCardHeights = this.equalizeProductCardHeights.bind(this);

    this.toggleButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', (event) => {
      // Disallowed characters (emoji/symbols) never enter the field at all —
      // stricter than the PRD 3.7 strip-before-search, per client request.
      // Skipped mid-IME-composition (would break multibyte input); the
      // compositionend listener below filters once composing finishes.
      if (!event.isComposing) this.enforceAllowedCharacters();
      const value = this.input.value;
      // Remember the FULL term: grow it while typing, but never shrink it on a
      // backspace (a value that's a prefix of what we already have = deletion).
      // So clearing "under 500" one character at a time still keeps "under 500".
      if (value.trim() && !this.fullTypedValue.startsWith(value)) {
        this.fullTypedValue = value;
      }
      // GA4 search_cleared: fires on the non-empty -> empty transition only
      // (user clearing), never on programmatic resets (PRD 7.2). Report the FULL
      // term (fullTypedValue), not just the last character left before empty.
      if (this.lastTypedValue.trim() && !value.trim()) {
        this.trackGA4('search_cleared', {
          search_term: (this.fullTypedValue || this.lastTypedValue).trim(),
          page_location: window.location.href,
        });
        this.fullTypedValue = '';
      }
      this.lastTypedValue = value;
      this.syncIdleVisibility();
    });
    this.input?.addEventListener('compositionend', () => {
      this.enforceAllowedCharacters();
      const value = this.input.value;
      if (value.trim() && !this.fullTypedValue.startsWith(value)) {
        this.fullTypedValue = value;
      }
      this.lastTypedValue = value;
      this.syncIdleVisibility();
    });
    this.form?.addEventListener('submit', () => {
      const term = this.input?.value?.trim();
      this.saveRecentSearch(term);
      // Stash the commit; `search` fires on the results page it opens
      // (fireStashedSearch) so it lands in that page's dataLayer.
      if (term) this.stashSearch(term, 'enter');
    });
    // GA4 search commits via tap: a recent term (source: recent), a
    // configured suggestion link, or a predictive result card (both
    // source: suggestion — PRD 7.4 defines suggestion as "tapped a
    // predictive result").
    this.addEventListener('click', (event) => {
      const idleLink = event.target.closest?.('.nav-search-idle__link');
      if (idleLink) {
        const isRecent = Boolean(idleLink.closest('[data-nav-search-recent-list]'));
        this.stashSearch(idleLink.textContent.trim(), isRecent ? 'recent' : 'suggestion');
        return;
      }
      const resultCard = event.target.closest?.('[data-nav-search-results-grid] .nav-search-product');
      if (resultCard) {
        const term = this.input?.value?.trim();
        if (term) this.stashSearch(term, 'suggestion');
        // Stash the tapped product so view_item fires on the PDP it opens.
        this.stashSearchViewItem(resultCard, 'predictive_overlay');
      }
    });
    // Fired by predictive-search.js when the search API call fails —
    // swap the dropdown for the designed error state (Figma 1237:2895).
    this.addEventListener('predictive-search:error', () => this.showSearchError());
    // Fired when a search returns zero products — show the designed
    // zero-results state (Figma 1237:2368: message + trending products).
    this.addEventListener('predictive-search:zero', (event) => this.showZeroResults(event.detail?.term));
    // Fired when a search request goes to the network / when its results
    // render — the in-flight window shows the full skeleton panel (Figma
    // 1237:6423); results lift the rendered cards into the panel's Products
    // section (Figma 1237:1759).
    this.addEventListener('predictive-search:loading', () => this.showLoading());
    this.addEventListener('predictive-search:results', () => this.showResults());

    this.recentList?.addEventListener('click', (event) => this.onRecentListClick(event));
    window.addEventListener('resize', this.equalizeProductCardHeights);

    this.renderRecentSearches();
    this.hydrateRecentSearches();

    // GA4 search (PRD 7.2): if the shopper committed a search on the previous
    // page, fire it now — on the page it landed on (SRP/PDP) — so it's in THIS
    // page's dataLayer, not the one left behind on navigation.
    this.fireStashedSearch();

    // GA4 view_item (PRD 7.2): if this page is a PDP opened from the search
    // overlay or the SRP, fire view_item from the product stashed at tap time.
    this.fireSearchViewItem();

    // SRP (Figma 1237:3778): the bar starts open with the committed query
    // visible (PRD 4.3), replacing Dawn's in-page search box. Silent open:
    // no focus trap or steal, no analytics, no close-on-outside-click — the
    // bar is persistent on this page. No panel shows, so the page dim
    // (which keys off panel visibility) stays off until the shopper
    // interacts.
    // DESKTOP ONLY: on mobile `is-open` expands the search into a full-screen
    // overlay (Figma 393), which would cover the results page. On mobile the
    // query stays visible in the SRP breadcrumb/title instead, and the
    // shopper opens the overlay by tapping search.
    // Price-filter SRP: swap the blanked q=* for a human "under ₹500" label in
    // the search box + title BEFORE the auto-open check, so the bar reopens
    // showing what was searched (and re-submitting re-parses it).
    this.applyPriceSrpLabel();

    if (
      this.input?.value?.trim() &&
      window.location.pathname.endsWith('/search') &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      this.classList.add('is-open');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * A price search ("under 500" / "over 1000") arrives on the SRP as
   * ?q=*&filter.v.price.lte=500. The "*" is blanked server-side (it's a
   * machine token); here we build a friendly label from the URL and show it as
   * the search-box value and the SRP <h1>, so the shopper sees "under ₹500"
   * (and editing + re-submitting re-parses it via predictive-search.js).
   */
  applyPriceSrpLabel() {
    if (!window.location.pathname.endsWith('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const lte = params.get('sb_dlte');
    const gte = params.get('sb_dgte');
    if (!lte && !gte) return;
    const q = (params.get('q') || '').trim();
    // Strip the trailing prefix "*" (buildPriceUrl adds it to the text part of
    // a "lip under 500" query) so the label reads "lip under ₹500", not "lip*".
    const term = q === '*' ? '' : q.replace(/\*+$/, '');
    const gteNum = gte ? parseInt(gte, 10) : null;
    const lteNum = lte ? parseInt(lte, 10) : null;
    // gte=1 is the "exclude ₹0 free items" sentinel, not a shopper-set floor —
    // so an "under" query keeps reading as "under ₹X", not "₹1–₹X".
    const hasFloor = gteNum && gteNum > 1;
    // Use the exact preposition the shopper typed (sb_prep from buildPriceUrl):
    // "below 200" -> "below ₹200", "under 200" -> "under ₹200". Falls back to
    // under/over when the param is absent (e.g. an older/hand-built URL).
    const prep = params.get('sb_prep');
    let phrase;
    if (lteNum && !hasFloor) phrase = `${prep || 'under'} ₹${lteNum}`;
    else if (hasFloor && !lteNum) phrase = `${prep || 'over'} ₹${gteNum}`;
    else phrase = `₹${gteNum}–₹${lteNum}`;
    const label = (term ? `${term} ${phrase}` : phrase).trim();
    if (this.input) this.input.value = label;
    const title = document.querySelector('.srp-title');
    if (title) title.textContent = label;
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.equalizeProductCardHeights);
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (this.isOpen()) return;

    this.classList.add('is-open');
    this.toggleButton?.setAttribute('aria-expanded', 'true');

    document.body.addEventListener('click', this.onBodyClick);
    document.body.addEventListener('keyup', this.onKeyUp);
    // Background scroll lock is applied by updateScrollLock() (called from
    // syncIdleVisibility below and every panel show/hide) so it also covers the
    // SRP, where the bar is persistently open but the panel only appears on
    // interaction.

    this.renderRecentSearches();
    this.hydrateRecentSearches();
    this.syncIdleVisibility();

    if (typeof trapFocus === 'function' && this.panel) {
      trapFocus(this.panel, this.input || this.panel);
    } else {
      this.input?.focus();
    }

    // GA4 search_bar_opened (debounced per PRD 7.6) + the blank-state
    // section view events, which only accompany a counted open.
    if (
      this.trackGA4('search_bar_opened', {
        page_title: document.title,
        page_location: window.location.href,
      })
    ) {
      this.trackBlankStateViews();
    }
  }

  /**
   * PRD 7.2 section-render events, fired only for sections that actually
   * rendered with content (7.6: no data, no event).
   */
  trackBlankStateViews() {
    const trendingCount = this.querySelectorAll(
      '.nav-search-idle__section--trending .nav-search-product'
    ).length;
    if (trendingCount > 0) {
      this.trackGA4('view_trending_products', {
        item_list_name: 'trending_products',
        item_count: trendingCount,
      });
    }

    const categoryCount = this.querySelectorAll('.nav-search-idle__pill').length;
    if (categoryCount > 0) {
      this.trackGA4('view_trending_categories', {
        list_name: 'trending_categories',
        item_count: categoryCount,
      });
    }

    const recentCount = this.recentSection?.hidden === false ? this.getRecentSearches().length : 0;
    if (recentCount > 0) {
      this.trackGA4('view_recent_searches', {
        list_name: 'recent_searches',
        search_count: recentCount,
      });
    }
  }

  close() {
    if (!this.isOpen()) return;

    this.classList.remove('is-open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');

    document.body.removeEventListener('click', this.onBodyClick);
    document.body.removeEventListener('keyup', this.onKeyUp);

    // is-open is gone → no panel is showing → release the scroll lock.
    this.updateScrollLock();

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.toggleButton);
    } else {
      this.toggleButton?.focus();
    }

    this.trackGA4('search_bar_closed', {
      page_title: document.title,
      page_location: window.location.href,
    });

    this.reset();
  }

  /**
   * Lock background scroll (desktop) whenever the overlay is actually SHOWING
   * content — the idle panel (suggestions/recent/trending or live results) or
   * the zero/error panels. Reactive to every panel toggle, so it covers the
   * SRP too, where the bar is persistently open but the panel only appears once
   * the shopper interacts (so results stay scrollable until then). The desktop
   * panel has its OWN inner scroll (≥990px: max-height + overflow-y:auto), so
   * the page never needs to move; we freeze <html> with overflow:hidden and pad
   * the removed scrollbar so the sticky header (which the overlay is anchored
   * to) doesn't shift. overflow:hidden — not the theme's position:fixed lock —
   * because position:fixed would push the header + overlay off-screen when the
   * page is scrolled. Mobile keeps its own full-screen sheet.
   */
  updateScrollLock() {
    const showing =
      window.matchMedia('(min-width: 990px)').matches &&
      this.isOpen() &&
      ((this.idlePanel && this.idlePanel.classList.contains('is-visible')) ||
        (this.zeroPanel && !this.zeroPanel.hidden) ||
        (this.errorPanel && !this.errorPanel.hidden));
    if (showing === Boolean(this.scrollLocked)) return;
    if (showing) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      if (scrollBarWidth > 0) document.documentElement.style.paddingRight = `${scrollBarWidth}px`;
      this.scrollLocked = true;
    } else {
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      this.scrollLocked = false;
    }
  }

  /**
   * GA4 wrapper (Search Bar PRD 7.5): every payload carries user_type and a
   * timestamp; same-name events are debounced (1s) against rapid repeats
   * (7.6). Prefers gtag, falls back to a dataLayer push, and never lets an
   * analytics failure break the search UI. Returns whether the event fired.
   */
  trackGA4(eventName, params) {
    const now = Date.now();
    if (now - (this.lastTrackedAt[eventName] || 0) < 1000) return false;
    this.lastTrackedAt[eventName] = now;

    const payload = {
      ...params,
      user_type: this.userType,
      timestamp: new Date().toISOString(),
    };
    // Send through the ONE global sender (snippets/ga4-base.liquid) so every
    // event is traced in a single place. Fallback to a direct push only if the
    // global helper somehow isn't defined yet (e.g. ga4-base didn't render).
    if (typeof window.gtagEvent === 'function') return window.gtagEvent(eventName, payload);
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...payload });
      }
    } catch (error) {
      // Analytics must never break the search experience.
    }
    return true;
  }

  /**
   * Stash the tapped product (overlay) so view_item can fire on the PDP it
   * opens. Position is the card's index in the live results grid (1-based).
   */
  stashSearchViewItem(card, listName) {
    if (!card) return;
    const cards = Array.from(this.resultsGrid?.querySelectorAll('.nav-search-product') || []);
    try {
      sessionStorage.setItem(
        NavSearch.VIEW_ITEM_KEY,
        JSON.stringify({
          item_id: card.dataset.productId || '',
          item_name: card.dataset.productTitle || '',
          item_list_name: listName,
          index: cards.indexOf(card) + 1 || 1,
          ts: Date.now(),
        })
      );
    } catch (error) {
      // sessionStorage can throw (private mode / quota) — never break the tap.
    }
  }

  /**
   * GA4 view_item (PRD 7.2): on a product page, if the shopper arrived by
   * tapping a product in the predictive overlay or on the SRP, fire view_item
   * carrying the originating item_list_name + index. Reads (and clears) the
   * stash written at tap time — here for the overlay, srp-infinite-scroll.js
   * for the SRP. Fires at most once per stashed tap.
   */
  fireSearchViewItem() {
    if (!/\/products\//.test(window.location.pathname)) return;
    let raw;
    try {
      raw = sessionStorage.getItem(NavSearch.VIEW_ITEM_KEY);
      sessionStorage.removeItem(NavSearch.VIEW_ITEM_KEY);
    } catch (error) {
      return;
    }
    if (!raw) return;
    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return;
    }
    // Freshness guard: a stale stash must not attach to an unrelated PDP visit.
    if (!data || !data.item_id || Date.now() - (data.ts || 0) > 60000) return;
    this.trackGA4('view_item', {
      item_list_name: data.item_list_name,
      items: [
        {
          item_id: data.item_id,
          item_name: data.item_name,
          item_list_name: data.item_list_name,
          index: data.index,
        },
      ],
    });
  }

  /**
   * Stash a committed search (enter / recent term / suggestion or result-card
   * tap). The `search` event is NOT sent here — fireStashedSearch() sends it on
   * the next page load, so it lands in the results page's dataLayer rather than
   * the page being navigated away from.
   */
  stashSearch(term, source) {
    const trimmed = (term || '').trim();
    if (!trimmed) return;
    try {
      sessionStorage.setItem(
        NavSearch.SEARCH_KEY,
        JSON.stringify({ search_term: trimmed, search_source: source, ts: Date.now() })
      );
    } catch (error) {
      // sessionStorage can throw (private mode / quota) — never break the commit.
    }
  }

  /**
   * Fire the GA4 `search` event on the page a committed search landed on (SRP
   * or PDP), from the stash written at commit time. Reads + clears the stash so
   * it fires at most once — and is present in THIS page's dataLayer.
   */
  fireStashedSearch() {
    let raw;
    try {
      raw = sessionStorage.getItem(NavSearch.SEARCH_KEY);
      sessionStorage.removeItem(NavSearch.SEARCH_KEY);
    } catch (error) {
      return;
    }
    if (!raw) return;
    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return;
    }
    // Freshness guard: a stale commit must not attach to an unrelated later visit.
    if (!data || !data.search_term || Date.now() - (data.ts || 0) > 60000) return;
    this.trackGA4('search', {
      search_term: data.search_term,
      search_source: data.search_source,
    });
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') this.close();
  }

  /**
   * The current field value sanitized exactly as predictive-search.js gates
   * the query. Below 3 searchable characters the blank state (recent /
   * trending) owns the panel. Used both to decide the blank state AND to reject
   * STALE in-flight responses: clearing/shortening the field doesn't abort a
   * fetch already on the wire, so it still resolves and — for the deleted term —
   * fires predictive-search:zero (or :results/:error). Without this gate that
   * late event clobbered the blank state and stranded the overlay in
   * "No products found" after the shopper had already emptied the box.
   */
  searchableQuery() {
    return (this.input?.value || '')
      .replace(/[^\p{L}\p{N}\s'-]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  syncIdleVisibility() {
    if (!this.idlePanel) return;
    // Any input change invalidates a previously shown API-failure or
    // zero-results state.
    this.hideSearchError();
    this.hideZeroResults();
    // Search Bar PRD 1.7/3.2: live search only starts at 3 searchable
    // characters — below that the blank state (recent searches, trending)
    // stays visible. Mirrors the sanitize+gate in predictive-search.js.
    const searchable = this.searchableQuery();
    const hasQuery = searchable.length >= 3;
    // The loading skeleton survives further typing (the next fetch is about
    // to fire) but never coexists with the blank state.
    if (!hasQuery) {
      this.hideLoading();
      this.hideResults();
    }
    // The panel stays up when live results are showing (Figma 1237:1759 —
    // the Products section lives inside it); otherwise it tracks the
    // blank-state rule.
    const resultsActive = this.resultsSection && !this.resultsSection.hidden;
    this.idlePanel.classList.toggle('is-visible', !hasQuery || resultsActive);
    if (hasQuery && !resultsActive) {
      // The blank state just hid but the (debounced) fetch hasn't fired yet
      // — show the loading skeleton right away so this window is never
      // blank. Skipped while results are open: refining a query keeps the
      // previous results on screen until new ones replace them.
      const predictive = this.querySelector('predictive-search');
      if (!predictive?.hasAttribute('open')) this.showLoading();
    }
    if (!hasQuery) {
      this.syncSkeletonState();
      // Wait a frame so the panel is display:flex before measuring card heights.
      // Re-run after images load in case aspect-ratio hadn't resolved yet.
      requestAnimationFrame(() => {
        this.equalizeProductCardHeights();
        this.querySelectorAll('.nav-search-product .pvc__img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', this.equalizeProductCardHeights, { once: true });
        });
      });
    }
    this.updateScrollLock();
  }

  /**
   * Skeleton loading state (Figma 1237:6423): while any trending-product
   * image is still fetching, swap the idle panel's real columns for the
   * skeleton (CSS keys off `is-loading`). Once every pending image settles
   * (load OR error — a broken image must not strand the skeleton) the real
   * content swaps back in. Two guards:
   * - the images are loading="lazy", and the skeleton hides the real cards
   *   (display:none), so they'd never intersect and never load — flip
   *   pending ones to eager so the fetch starts immediately;
   * - a hard timeout so one stalled request can't hold the panel hostage.
   */
  syncSkeletonState() {
    if (!this.idlePanel || !this.idlePanel.querySelector('.nav-search-skeleton')) return;

    const pending = [...this.querySelectorAll('.nav-search-product .pvc__img')].filter(
      (img) => !img.complete
    );
    if (!pending.length) {
      this.idlePanel.classList.remove('is-loading');
      return;
    }

    this.idlePanel.classList.add('is-loading');

    let settled = 0;
    const onImageSettled = () => {
      settled += 1;
      if (settled >= pending.length) this.finishSkeletonLoading();
    };
    pending.forEach((img) => {
      img.loading = 'eager';
      img.addEventListener('load', onImageSettled, { once: true });
      img.addEventListener('error', onImageSettled, { once: true });
    });

    clearTimeout(this.skeletonTimeout);
    this.skeletonTimeout = setTimeout(() => this.finishSkeletonLoading(), 4000);
  }

  finishSkeletonLoading() {
    clearTimeout(this.skeletonTimeout);
    if (!this.idlePanel?.classList.contains('is-loading')) return;
    this.idlePanel.classList.remove('is-loading');
    // The real cards were display:none while the skeleton showed, so their
    // heights couldn't be measured — equalize now that they're back.
    requestAnimationFrame(this.equalizeProductCardHeights);
  }

  /**
   * Make every trending-product card the same height as the tallest one,
   * with no inner scrollbar. CSS grid stretch alone only equalizes per-row;
   * this covers both rows so the grid looks uniform.
   */
  equalizeProductCardHeights() {
    if (!this.idlePanel?.classList.contains('is-visible')) return;

    const cards = this.querySelectorAll('.nav-search-product');
    if (!cards.length) return;

    cards.forEach((card) => {
      card.style.minHeight = '';
    });

    let tallest = 0;
    cards.forEach((card) => {
      tallest = Math.max(tallest, card.getBoundingClientRect().height);
    });

    if (tallest <= 0) return;

    cards.forEach((card) => {
      card.style.minHeight = `${Math.ceil(tallest)}px`;
    });
  }

  /**
   * Strip disallowed characters from the field itself — letters (any
   * script), numbers, spaces, hyphens and apostrophes survive; emoji and
   * other symbols are dropped as typed or pasted. Mirrors the sanitize
   * pattern predictive-search.js applies to the outgoing query (kept there
   * too as defense in depth). Caret position is preserved so filtering a
   * mid-word paste doesn't jump the cursor to the end.
   */
  enforceAllowedCharacters() {
    const input = this.input;
    if (!input) return;
    const original = input.value;
    const cleaned = original.replace(/[^\p{L}\p{N}\s'-]/gu, '');
    if (cleaned === original) return;
    const caret = input.selectionStart ?? cleaned.length;
    const removedBeforeCaret =
      original.slice(0, caret).length - original.slice(0, caret).replace(/[^\p{L}\p{N}\s'-]/gu, '').length;
    input.value = cleaned;
    const newCaret = Math.max(0, caret - removedBeforeCaret);
    input.setSelectionRange(newCaret, newCaret);
  }

  showLoading() {
    // Typing/loading state (Figma 1237:6423): the idle panel's own skeleton
    // doubles as the live-search loader — show the panel forced into its
    // skeleton mode until the request settles.
    if (!this.idlePanel?.querySelector('.nav-search-skeleton')) return;
    this.hideSearchError();
    this.hideZeroResults();
    this.idlePanel.classList.add('is-visible', 'is-loading');
    this.updateScrollLock();
  }

  hideLoading() {
    // Only lifts the skeleton overlay; whether the idle panel itself stays
    // visible is decided by the caller (results hide it, the blank state
    // keeps it and syncSkeletonState re-evaluates the image loader).
    this.idlePanel?.classList.remove('is-loading');
  }

  showSearchError() {
    if (!this.errorPanel) return;
    // Stale-response guard (see searchableQuery): a late error for an
    // already-cleared term must not replace the blank state.
    if (this.searchableQuery().length < 3) {
      this.syncIdleVisibility();
      return;
    }
    this.errorPanel.hidden = false;
    this.hideLoading();
    this.hideZeroResults();
    this.hideResults();
    this.idlePanel?.classList.remove('is-visible');
    this.updateScrollLock();
  }

  hideSearchError() {
    if (this.errorPanel) this.errorPanel.hidden = true;
  }

  showZeroResults(term) {
    if (!this.zeroPanel) return;
    // Stale-response guard: the field has since been cleared/shortened below
    // the search threshold, so this zero belongs to a term the shopper already
    // deleted — the blank state owns the panel now. Restore it and bail.
    if (this.searchableQuery().length < 3) {
      this.syncIdleVisibility();
      return;
    }
    if (this.zeroTerm) this.zeroTerm.textContent = term || '';
    this.zeroPanel.hidden = false;
    this.hideLoading();
    this.hideSearchError();
    this.hideResults();
    this.idlePanel?.classList.remove('is-visible');
    this.updateScrollLock();
  }

  hideZeroResults() {
    if (this.zeroPanel) this.zeroPanel.hidden = true;
  }

  /**
   * Live results (Figma 1237:1759): the predictive-search section rendered
   * the matched products as compact cards inside Dawn's (CSS-hidden)
   * dropdown — lift them into the panel's Products section, point See All
   * at the committed-search URL for the current term, and keep the whole
   * idle layout (suggestions / recent / categories / trending) around them.
   */
  showResults() {
    if (!this.resultsSection || !this.resultsGrid) return;
    // Stale-response guard (see searchableQuery): a late results event for a
    // term the shopper already cleared must not reopen the panel over the
    // blank state.
    if (this.searchableQuery().length < 3) {
      this.syncIdleVisibility();
      return;
    }
    const allCards = [...this.querySelectorAll('#predictive-search-results-products-list .nav-search-product')];
    if (!allCards.length) return; // zero state owns this path via its own event

    const term = this.input?.value?.trim() || '';
    const predictive = this.querySelector('predictive-search');
    const price = predictive?.parsePriceQuery?.(term);

    // Search & Discovery synonyms + boosts: Shopify's predictive results already
    // apply the store's synonym mappings and product boosts, so show them as
    // returned (in Shopify's boosted order) instead of re-filtering client-side
    // against the field allowlist. That allowlist filter used to drop any synonym
    // match whose term wasn't in the product's own fields (e.g. the
    // "icecream" -> Poutie synonym); removed here so synonyms/boosts win.
    // Trade-off: description-only matches Shopify returns are no longer stripped.
    // Price ("under/above") queries bypass the predictive endpoint (below).
    // (Zero products never reaches here: predictive-search:zero fires first and
    //  opens the trending / no-results panel.)
    let cards = allCards;
    if (!price && !cards.length) {
      this.showZeroResults(term);
      return;
    }

    // Suggestions (Figma 1237:8722) = the matched/synonym-matched PRODUCT names,
    // typed prefix bold. Price ("under/above") queries show NO suggestions.
    if (price) this.hideSuggestions();
    else this.renderSuggestions(term, cards);

    // Dawn renders them tabindex="-1" for its listbox pattern; lifted into
    // the panel they're ordinary links and must be tab-reachable.
    cards.forEach((card) => card.removeAttribute('tabindex'));
    this.resultsGrid.replaceChildren(...cards);

    if (this.resultsSeeAll) {
      // Keep the See All href current for the WHOLE matched set. Its VISIBILITY
      // is owned by CSS (shown only past 3 products on web / 4 on mweb, via a
      // :has() child-count gate) so it tracks the viewport with no resize
      // handler here. All matched cards are lifted into the grid; the overflow
      // beyond the cap is hidden by CSS and reached through See All.
      const action = this.form?.getAttribute('action') || '/search';
      // For a price query ("above 2000"), See All must land on the same
      // price-filtered SRP that Enter builds — not a text search for the raw
      // phrase. Reuse the <predictive-search> element's own URL builder.
      this.resultsSeeAll.href = price
        ? predictive.buildPriceUrl(price)
        : `${action}?q=${encodeURIComponent(term)}*&type=product`;
    }

    this.resultsSection.hidden = false;
    this.hideLoading();
    this.hideSearchError();
    this.hideZeroResults();
    // is-results trims the trending section to a single row (3 cards) while
    // live results are on screen — Figma 1237:1759 vs the idle state's 6.
    this.idlePanel?.classList.add('is-visible', 'is-results');
    requestAnimationFrame(this.equalizeProductCardHeights);

    // GA4 view_item_list (PRD 7.2, item_list_name: predictive_overlay) —
    // once per distinct product set, not on every keystroke re-render.
    const items = cards.map((card, index) => ({
      item_id: card.dataset.productId || '',
      item_name: card.dataset.productTitle || '',
      index: index + 1,
    }));
    const signature = items.map((item) => item.item_id).join(',');
    if (signature && signature !== this.lastResultsSignature) {
      this.lastResultsSignature = signature;
      this.trackGA4('view_item_list', { item_list_name: 'predictive_overlay', items });
    }
    this.updateScrollLock();
  }

  /**
   * True when a product's NAME matches the typed query: every whitespace-
   * separated word the shopper typed must be the prefix of some word in the
   * product title (case-insensitive). Mirrors how prefix search reads the
   * name, so "sun" matches "Sunnyside" and "Sun" but a product that only had
   * "sun" in its description/tags does not. Used to keep the overlay's product
   * list to genuine name matches (Search Bar overlay rule).
   */
  titleMatchesQuery(title, query) {
    const q = (query || '').toLowerCase().trim();
    if (!q) return false;
    const titleWords = (title || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!titleWords.length) return false;
    return q
      .split(/\s+/)
      .filter(Boolean)
      .every((token) => titleWords.some((word) => word.startsWith(token)));
  }

  hideResults() {
    if (!this.resultsSection) return;
    this.resultsSection.hidden = true;
    this.resultsGrid?.replaceChildren();
    this.idlePanel?.classList.remove('is-results');
    // No results on screen (blank state, zero-results, error) → no suggestions.
    this.hideSuggestions();
  }

  /**
   * Live suggestions (Figma 1237:8722): the matched — or synonym-matched —
   * PRODUCT names (up to 3), each linking to its product, with the typed prefix
   * bold and the completion regular. Shown only while typing a non-price query
   * that matched something; no match → hidden (not sticky). Price queries and
   * the blank state show none (handled by the caller / hideSuggestions).
   */
  renderSuggestions(term, cards) {
    if (!this.suggestionsDynamic || !this.suggestionsList) return;
    const items = (cards || [])
      .slice(0, 3)
      .map((card) => {
        // Name ONLY — the part before "…: description" (e.g. "DOUBLE TAKE" out of
        // "DOUBLE TAKE: Lash Boosting Care Serum…"), Title Cased for display.
        const name = this.titleCase((card.dataset.productTitle || '').split(':')[0].trim());
        return { name, href: card.getAttribute('href') || '#' };
      })
      .filter((item) => item.name);
    if (!items.length) {
      this.hideSuggestions();
      return;
    }
    this.suggestionsList.innerHTML = items
      .map(
        (item) =>
          `<li class="nav-search-idle__list-item"><a href="${this.escapeHtml(item.href)}" class="nav-search-idle__link nav-search-idle__link--product">${this.highlightMatch(item.name, term)}</a></li>`
      )
      .join('');
    this.suggestionsDynamic.hidden = false;
  }

  /** "DOUBLE TAKE" / "poutie" -> "Double Take" / "Poutie" for suggestion display. */
  titleCase(value) {
    return value
      .split(/\s+/)
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
      .join(' ');
  }

  /** Hide + clear the live suggestions (blank state, price queries, no match). */
  hideSuggestions() {
    if (this.suggestionsDynamic) this.suggestionsDynamic.hidden = true;
    if (this.suggestionsList) this.suggestionsList.replaceChildren();
  }

  /** Escape a product title and wrap the first case-insensitive occurrence of
   *  the typed term in <strong>. No highlight for synonym/tag matches where the
   *  term doesn't appear in the title. */
  highlightMatch(title, term) {
    const t = (term || '').trim();
    if (!t) return this.escapeHtml(title);
    const idx = title.toLowerCase().indexOf(t.toLowerCase());
    if (idx === -1) return this.escapeHtml(title);
    return (
      this.escapeHtml(title.slice(0, idx)) +
      '<strong>' +
      this.escapeHtml(title.slice(idx, idx + t.length)) +
      '</strong>' +
      this.escapeHtml(title.slice(idx + t.length))
    );
  }

  reset() {
    if (this.input) this.input.value = '';
    this.syncIdleVisibility();
  }

  /**
   * Recent searches (Search Bar PRD 2.2). Split by identity:
   * - logged-out (no customer id): browser localStorage, device-scoped.
   * - logged-in: the backend, keyed to the customer id so history follows the
   *   shopper across devices. Called DIRECTLY — NO app proxy, NO signature:
   *   the theme passes the Shopify customer id and the backend trusts it.
   * The in-memory list renders in both modes; server mode hydrates on open and
   * makes optimistic local updates, silently falling back if the backend is
   * unreachable. Which backend/DB it hits is just `recentApiBase` (the header
   * `recent_search_api_base` setting) — local for a test theme, prod for live.
   */
  // GET  <base>/api/sammmmfam/recent-searches?customer_id=ID           -> list
  // POST <base>/api/sammmmfam/recent-searches?customer_id=ID
  //      (form-encoded: op=save|remove & search_query=TERM)            -> save/remove
  // Form-encoded POST + GET are CORS "simple" requests, so no preflight fires.
  /**
   * The logged-in Shopify customer id, read at CALL TIME (not cached at
   * construction) so it works even when the login provider fills it in after
   * this element renders. Prefers the theme's native {{ customer.id }}
   * (data-customer-id); falls back to KwikPass — this storefront logs shoppers
   * in via KwikPass, which creates NO native Shopify customer session (so
   * {{ customer.id }} stays empty) and instead stores the Shopify customer id
   * in localStorage `kp_customer_id`.
   */
  recentCustomerId() {
    const native = (this.dataset.customerId || '').trim();
    if (native) return native;
    try {
      return (window.localStorage.getItem('kp_customer_id') || '').trim();
    } catch (error) {
      return '';
    }
  }

  recentEndpoint() {
    return `${this.recentApiBase}/api/sammmmfam/recent-searches?customer_id=${encodeURIComponent(this.recentCustomerId())}`;
  }

  get usesServerRecents() {
    // Server storage only when we can identify the shopper (native or KwikPass).
    return Boolean(this.recentCustomerId());
  }

  getRecentSearches() {
    if (this.recentSearches) return this.recentSearches;
    try {
      const raw = window.localStorage.getItem(NavSearch.STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      this.recentSearches = Array.isArray(list) ? list : [];
    } catch (error) {
      this.recentSearches = [];
    }
    return this.recentSearches;
  }

  setRecentSearches(list) {
    this.recentSearches = list;
    // Logged-in history lives server-side only — not mirrored to
    // localStorage, so a shared device never leaks one account's searches
    // into another session's blank state.
    if (this.usesServerRecents) return;
    try {
      window.localStorage.setItem(NavSearch.STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // localStorage unavailable (private mode, quota, etc.) — fail silently.
    }
  }

  hydrateRecentSearches() {
    if (!this.usesServerRecents) return;
    fetch(this.recentEndpoint(), { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((data) => {
        const terms = Array.isArray(data?.searches) ? data.searches : [];
        this.recentSearches = terms
          .map((entry) => (typeof entry === 'string' ? entry : entry?.search_term || ''))
          .filter(Boolean)
          .slice(0, NavSearch.MAX_RECENT);
        this.renderRecentSearches();
      })
      .catch(() => {
        // Backend unreachable — the localStorage-seeded list already rendered,
        // so the shopper still sees something sensible.
      });
  }

  proxyWrite(op, term) {
    // Form-encoded (a CORS "simple" content type) so no preflight is needed.
    fetch(this.recentEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ op, search_query: term }).toString(),
    }).catch(() => {
      // Fire-and-forget: the optimistic in-memory update already happened.
    });
  }

  saveRecentSearch(term) {
    const trimmed = (term || '').trim();
    if (!trimmed) return;

    const existing = this.getRecentSearches().filter(
      (entry) => entry.toLowerCase() !== trimmed.toLowerCase()
    );
    existing.unshift(trimmed);

    this.setRecentSearches(existing.slice(0, NavSearch.MAX_RECENT));
    if (this.usesServerRecents) this.proxyWrite('save', trimmed);
  }

  removeRecentSearch(term) {
    const remaining = this.getRecentSearches().filter((entry) => entry !== term);
    this.setRecentSearches(remaining);
    if (this.usesServerRecents) this.proxyWrite('remove', term);
    this.renderRecentSearches();
  }

  onRecentListClick(event) {
    const removeButton = event.target.closest('[data-nav-search-recent-remove]');
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();
    this.removeRecentSearch(removeButton.dataset.navSearchRecentRemove);
  }

  renderRecentSearches() {
    if (!this.recentList || !this.recentSection) return;

    const recent = this.getRecentSearches();
    this.recentSection.hidden = recent.length === 0;
    this.recentList.innerHTML = recent
      .map(
        (term) => `
          <li class="nav-search-idle__list-item">
            <a href="${this.searchUrl(term)}" class="nav-search-idle__link">${this.escapeHtml(term)}</a>
            <button
              type="button"
              class="nav-search-idle__remove"
              aria-label="Remove ${this.escapeHtml(term)} from recent searches"
              data-nav-search-recent-remove="${this.escapeHtml(term)}"
            >
              <span class="svg-wrapper">${this.closeIconMarkup()}</span>
            </button>
          </li>
        `
      )
      .join('');
  }

  searchUrl(term) {
    const action = this.form?.getAttribute('action') || '/search';
    // Scope committed search to products only (no pages/articles in results).
    return `${action}?q=${encodeURIComponent(term)}&type=product`;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  closeIconMarkup() {
    if (!this._closeIconMarkup) {
      const existing = this.querySelector('.nav-search__icon-btn--close .icon, .nav-search__icon-btn--close svg');
      this._closeIconMarkup = existing ? existing.outerHTML : '';
    }
    return this._closeIconMarkup;
  }
}

customElements.define('nav-search', NavSearch);
